import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, QrCode, CreditCard, ShieldCheck, FlaskConical, FileText, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { brl, useSeo } from "@/lib/fmt";
import ProductCard from "@/components/store/ProductCard";
import { Newsletter } from "@/components/store/Footer";

const CHIPS = ["sono", "digestao", "energia", "imunidade", "chas", "kits"];

function Hero({ banner, hero }) {
  const { add } = useStore();
  return (
    <section className="container-na pt-6 lg:pt-10">
      <div className="grain relative grid overflow-hidden rounded-[36px] bg-na-sage2 lg:grid-cols-2" data-testid="home-hero">
        <div className="relative z-10 flex flex-col justify-center p-8 sm:p-12 lg:p-16">
          <p className="eyebrow fade-up">Carro-chefe · Suplemento alimentar</p>
          <h1 className="fade-up mt-4 text-4xl leading-[1.05] text-na-ink sm:text-5xl lg:text-6xl" style={{ animationDelay: ".08s" }}>{banner?.titulo || "Magnésio Quelado 4 em 1"}</h1>
          <p className="fade-up mt-5 max-w-md text-base text-na-ink2 md:text-lg" style={{ animationDelay: ".16s" }}>{banner?.subtitulo}</p>
          {hero && (
            <div className="fade-up mt-6 flex items-baseline gap-3" style={{ animationDelay: ".22s" }}>
              {hero.preco_de && <span className="text-na-ink2 line-through">{brl(hero.preco_de)}</span>}
              <span className="text-3xl font-bold">{brl(hero.preco)}</span>
              <span className="text-sm font-semibold text-na-forest">{brl(hero.preco_pix)} no Pix</span>
            </div>
          )}
          <div className="fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: ".3s" }}>
            {hero && <button className="btn-na" onClick={() => add(hero.slug, 1, 1, false, "drawer", hero.preco)} data-testid="hero-add-button">Adicionar à sacola</button>}
            <Link to={banner?.link || "/produto/magnesio-4-em-1"} className="btn-line" data-testid="hero-details-link">Conhecer <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
        <img src={fileUrl(banner?.imagem)} alt="Frasco Nativa Amazon sobre madeira clara com folhas" className="h-72 w-full object-cover sm:h-96 lg:h-full lg:min-h-[520px]" />
      </div>
    </section>
  );
}

const Benefits = ({ s }) => (
  <section className="container-na mt-6 grid gap-3 sm:grid-cols-3" data-testid="benefits-strip">
    {[[Truck, `Frete grátis acima de ${brl(s?.frete_gratis_acima || 199)}`], [QrCode, `${s?.desconto_pix || 5}% de desconto no Pix`], [CreditCard, `Até ${s?.parcelas_max || 3}x sem juros`]].map(([I, t]) => (
      <div key={t} className="flex items-center gap-3 rounded-full border border-na-line bg-white px-5 py-3.5 text-sm font-medium"><I className="h-5 w-5 text-na-gold" />{t}</div>
    ))}
  </section>
);

export default function Home() {
  const { meta, settings } = useStore();
  const [banners, setBanners] = useState([]);
  const [need, setNeed] = useState(null);
  const [grid, setGrid] = useState([]);
  const [all, setAll] = useState([]);
  const [posts, setPosts] = useState([]);
  useSeo(null, "Suplementos, fitoterápicos regularizados, chás e kits naturais. Frete grátis acima de R$ 199 e 5% off no Pix.");
  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data));
    api.get("/products").then((r) => setAll(r.data));
    api.get("/blog").then((r) => setPosts(r.data));
  }, []);
  useEffect(() => {
    api.get("/products", { params: need ? { need } : {} }).then((r) => setGrid(r.data));
  }, [need]);
  const hero = all.find((p) => p.slug === "magnesio-4-em-1");
  const kits = all.filter((p) => p.forma === "kit");
  return (
    <div>
      <Hero banner={banners[0]} hero={hero} />
      <Benefits s={settings} />
      <section className="container-na mt-20" data-testid="need-selector">
        <p className="eyebrow">Para a sua rotina</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Do que você precisa hoje?</h2>
        <div className="no-scrollbar -mx-4 mt-6 flex gap-2 overflow-x-auto px-4">
          <button className={`chip ${!need ? "chip-on" : ""}`} onClick={() => setNeed(null)} data-testid="need-chip-todos">Todos</button>
          {CHIPS.map((k) => meta.needs.find((n) => n.chave === k)).filter(Boolean).map((n) => (
            <button key={n.chave} className={`chip ${need === n.chave ? "chip-on" : ""}`} onClick={() => setNeed(n.chave)} data-testid={`need-chip-${n.chave}`}>{n.nome}</button>
          ))}
        </div>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4" data-testid="home-product-grid">
          {grid.map((p) => <ProductCard key={p.slug} p={p} />)}
        </div>
      </section>
      <section className="container-na mt-20">
        <div className="flex items-end justify-between"><h2 className="font-serif text-3xl">Mais vendidos</h2><Link to="/busca" className="text-sm font-semibold text-na-forest">Ver todos</Link></div>
        <div className="no-scrollbar -mx-4 mt-6 flex gap-4 overflow-x-auto px-4 pb-2">
          {all.filter((p) => p.forma !== "kit").slice(0, 6).map((p) => <div key={p.slug} className="w-64 shrink-0"><ProductCard p={p} /></div>)}
        </div>
      </section>
      <section className="container-na mt-20">
        <h2 className="font-serif text-3xl">Kits prontos</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">{kits.map((p) => <ProductCard key={p.slug} p={p} />)}</div>
      </section>
      <section className="mt-24 bg-white py-20" data-testid="how-we-choose">
        <div className="container-na grid gap-10 lg:grid-cols-[1fr_2fr]">
          <div><p className="eyebrow">Curadoria</p><h2 className="mt-2 font-serif text-3xl sm:text-4xl">Como escolhemos o que entra na loja</h2></div>
          <div className="grid gap-4 sm:grid-cols-3">
            {[[ShieldCheck, "Produto regularizado", "Cada item tem categoria regulatória e registro ou notificação quando exigido."], [FlaskConical, "Laudo por lote", "Recebemos cada lote com nota e laudo, e controlamos a validade."], [FileText, "Informação completa", "Composição, modo de uso e advertências sempre visíveis."]].map(([I, t, d]) => (
              <div key={t} className="rounded-[28px] bg-na-sage2 p-6"><I className="h-6 w-6 text-na-forest" /><h3 className="mt-4 font-serif text-lg">{t}</h3><p className="mt-2 text-sm text-na-ink2">{d}</p></div>
            ))}
          </div>
        </div>
      </section>
      <section className="container-na mt-20">
        <div className="flex items-end justify-between"><h2 className="font-serif text-3xl">Guia das plantas</h2><Link to="/blog" className="text-sm font-semibold text-na-forest">Ver o guia</Link></div>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {posts.slice(0, 2).map((b) => (
            <Link key={b.slug} to={`/blog/${b.slug}`} className="group overflow-hidden rounded-[28px] border border-na-line bg-white" data-testid={`home-post-${b.slug}`}>
              <img src={fileUrl(b.imagem)} alt="" loading="lazy" className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="p-6"><h3 className="font-serif text-xl">{b.titulo}</h3><p className="mt-2 text-sm text-na-ink2">{b.resumo}</p></div>
            </Link>
          ))}
        </div>
      </section>
      <section className="container-na mt-20">
        <div className="grid items-center gap-8 rounded-[36px] bg-na-acai p-8 sm:p-12 lg:grid-cols-2">
          <div><h2 className="font-serif text-3xl text-white">Ganhe 10% na primeira compra</h2><p className="mt-2 text-white/80">Receba o guia das plantas e novidades. Sem spam.</p></div>
          <Newsletter />
        </div>
      </section>
    </div>
  );
}
